import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { dateFormats, getDateFormat } from '../../utils/date';
import { translate } from '../../utils/translate';
import { setIncomingMessages } from '../ChatsList/chatListSlice';
import { getAlarmData, getIsLoading, getLocationData, setAlarmData } from './PanicButtonSlice';
import { createAlarm, deactivateAlarm, clearCreateData } from './PanicButtonSlice/actionCreators';
import type { PanicAlarmActivationData } from './PanicButtonSlice/types';
import { PanicAlarmState } from './PanicButtonSlice/types';
import {
  SLoaderContainer,
  SMsgText,
  STimerBtn,
  STimerContent,
  STimerOperationsContainer,
  STimerScreenContainer,
  STimerText,
} from './panicButton.style';
import { localStorageGet, localStorageRemove, localStorageSet } from '../../utils/storage';

enum DisplayedButton {
  Recall = 'recall',
  Unlock = 'unlock',
}

const LONG_PRESS_DELAY = 200;

export const PanicButtonTimer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const alarmData = useAppSelector(getAlarmData);
  const isLoading = useAppSelector(getIsLoading);
  const location = useAppSelector(getLocationData);

  const secondsForCountDown = 3;
  const [currentTime, setCurrentTime] = useState('');
  const [isLongPressActive, setIsLongPressActive] = useState(false);
  const [countDownSeconds, setCountDownSeconds] = useState(secondsForCountDown);
  const [displayedButton, setDisplayedButton] = useState<DisplayedButton | null>(null);
  const [alarmTriggerText, setAlarmTriggerText] = useState(null);
  const longPressStartTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const displayedButtonTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const countDownIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const isSendTriggeredRef = React.useRef(false);

  const setDisplayedButtonTimeout = () => {
    displayedButtonTimeoutRef.current = setTimeout(() => {
      setDisplayedButton(null);
    }, 3000);
  };
  const clearDisplayedButtonTimeout = () => {
    if (displayedButtonTimeoutRef.current) clearTimeout(displayedButtonTimeoutRef.current);
  };

  const clearLongPressStartTimeout = () => {
    if (longPressStartTimeoutRef.current) clearTimeout(longPressStartTimeoutRef.current);
  };

  const clearCountDownInterval = () => {
    if (countDownIntervalRef.current) clearInterval(countDownIntervalRef.current);
  };

  const enableBtn = (btnType: DisplayedButton) => {
    if (!isLongPressActive) {
      setDisplayedButton(btnType);
      setDisplayedButtonTimeout();
    }
  };

  const sendAlarm = () => {
    clearCountDownInterval();
    if ((!alarmData && !location) || isSendTriggeredRef.current) {
      return;
    }
    isSendTriggeredRef.current = true;
    dispatch(
      createAlarm((isSuccess: boolean) => {
        if (isSuccess) {
          enableBtn(DisplayedButton.Unlock);
          setAlarmTriggerText(t('alarm_succesfully_triggered'));
        } else {
          setAlarmTriggerText(t('alarm_sending_failed'));
        }
        setTimeout(() => {
          setAlarmTriggerText(null);
          setIsLongPressActive(false);
        }, 2000);
      })
    );
  };

  useEffect(() => {
    const panicButtonDataFromStorage = localStorageGet('panicButtonData');
    if (!panicButtonDataFromStorage) {
      localStorageSet(
        'panicButtonData',
        JSON.stringify({
          alarmData: JSON.stringify(alarmData),
          location: JSON.stringify(location),
        })
      );
    }
    const interval = setInterval(() => {
      setCurrentTime(`${getDateFormat(new Date().toISOString(), dateFormats.simpleTime24)}`);
    }, 1000);
    return () => {
      clearInterval(interval);
      dispatch(clearCreateData());
      clearDisplayedButtonTimeout();
      clearCountDownInterval();
    };
  }, []);

  const onScreenPress = () => {
    if (isLongPressActive) return;
    if (!location && !alarmData) {
      enableBtn(DisplayedButton.Unlock);
    }
    if (alarmData && !displayedButton) {
      if (alarmData.alarmStatus === PanicAlarmState.Unlocked) {
        enableBtn(DisplayedButton.Unlock);
      } else {
        enableBtn(DisplayedButton.Recall);
      }
    }
  };

  const onMouseDown = () => {
    const dontHaveAlarmAndLocation = !location && !alarmData;
    const isUnlocked = alarmData?.alarmStatus === PanicAlarmState.Unlocked;
    if (dontHaveAlarmAndLocation || isUnlocked) return;

    longPressStartTimeoutRef.current = setTimeout(() => {
      setIsLongPressActive(true);
      setCountDownSeconds(secondsForCountDown);
      clearDisplayedButtonTimeout();
      setDisplayedButton(null);
      countDownIntervalRef.current = setInterval(() => {
        setCountDownSeconds(prevSeconds => {
          if (prevSeconds > 0) {
            return +prevSeconds.toFixed(0) - 1;
          } else {
            sendAlarm();
            return 0;
          }
        });
      }, 1000);
    }, LONG_PRESS_DELAY);
  };

  const onMouseUp = () => {
    clearLongPressStartTimeout();
    setTimeout(() => {
      setIsLongPressActive(false);
    }, 50);

    clearCountDownInterval();
  };

  const onDeactivateAlarm = () => {
    localStorageRemove('panicButtonData');
    if (!alarmData) return;
    const deactivateAlarmData: PanicAlarmActivationData = {
      text: alarmData.text,
      groupIds: alarmData.groupIds,
      userIds: alarmData.recipientIds,
    };
    dispatch(deactivateAlarm(deactivateAlarmData, navigate, '/dashboard'));
  };

  const onUnlockAlarm = () => {
    localStorageRemove('panicButtonData');
    if (!alarmData) {
      navigate('/');
      return;
    }
    navigate(`/message/${alarmData.messageId}`);
    dispatch(setAlarmData(null));
    dispatch(setIncomingMessages(true));
  };

  if (isLoading) {
    return (
      <SLoaderContainer role="loaderContainer">
        <Loader />
      </SLoaderContainer>
    );
  }

  return (
    <STimerScreenContainer
      onClick={onScreenPress}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      role="timerScreenContainer"
    >
      <STimerContent role="timerContent">
        {isLongPressActive && countDownSeconds > 0 && (
          <STimerText role="countDownSeconds">{countDownSeconds.toString()}</STimerText>
        )}
        <STimerOperationsContainer role="operationsList">
          {alarmTriggerText && <SMsgText role="triggerText">{alarmTriggerText}</SMsgText>}
          {!displayedButton && !isLongPressActive && (
            <STimerText role="currentTime">{currentTime}</STimerText>
          )}
          {displayedButton === DisplayedButton.Recall && !isLongPressActive && (
            <STimerBtn className="recall-btn" onClick={onDeactivateAlarm} role="deactivateButton">
              {translate('alarm_deactivate')}
            </STimerBtn>
          )}
          {displayedButton === DisplayedButton.Unlock && (
            <STimerBtn className="deactive-btn" onClick={onUnlockAlarm} role="recallButton">
              {translate('alarm_unlock')}
            </STimerBtn>
          )}
        </STimerOperationsContainer>
      </STimerContent>
    </STimerScreenContainer>
  );
};
